import React, { useState } from 'react';
import { Button, Input, Form, Select, Row, Col, Typography, Modal, InputNumber, notification, Tag, Popconfirm } from 'antd';
import { SearchOutlined, DeleteOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getListFeaturesRequest, createFeatureRequest, updateFeatureRequest, deleteFeatureRequest } from '../../../services/cms/featureServiceRequest';
import UploadButton from '../../../components/UploadButton';
import { newObjectKeys } from '../../../utils/utility';

const { Text } = Typography;
const { TextArea } = Input;

const TYPE_OPTIONS = [
  { value: 1, label: 'Có phí' },
  { value: 2, label: 'Miễn phí' },
];

const SERVICE_TYPE_OPTIONS = [
  { value: 1, label: 'Dịch vụ 1-1' },
  { value: 2, label: 'Dịch vụ InApp' },
];

const STATUS_OPTIONS = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Deactive' },
];

const SHOW_OPTIONS = [
  { value: 1, label: 'Hiện' },
  { value: 2, label: 'Ẩn' },
];

const QuanLyTinhNang = () => {
  const [formSearch] = Form.useForm();
  const [formEdit] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const queryClient = useQueryClient();

  const { data: dataFeatures } = useQuery({
    queryKey: ['dataFeatures', params],
    queryFn: () => getListFeaturesRequest(params),
  });

  // Interceptor axios chỉ tự hiện notification cho HTTP 400 và reject bằng `error.response`.
  // Không có onError thì mọi lỗi khác (đặc biệt 500) làm modal đứng im, không báo gì (QA-11).
  const notifyMutationError = (err) =>
    notification.error({ message: err?.data?.message || err?.message || 'Có lỗi xảy ra!' });

  const mutationCreate = useMutation({
    mutationFn: createFeatureRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Tạo tính năng thành công!' });
        queryClient.invalidateQueries({ queryKey: ['dataFeatures'] });
        setModalVisible(false);
        formEdit.resetFields();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
      }
    },
    onError: notifyMutationError,
  });

  const mutationUpdate = useMutation({
    mutationFn: updateFeatureRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Cập nhật tính năng thành công!' });
        queryClient.invalidateQueries({ queryKey: ['dataFeatures'] });
        setModalVisible(false);
        formEdit.resetFields();
        setEditingFeature(null);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
      }
    },
    onError: notifyMutationError,
  });

  const mutationDelete = useMutation({
    mutationFn: deleteFeatureRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Xóa tính năng thành công!' });
        queryClient.invalidateQueries({ queryKey: ['dataFeatures'] });
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
      }
    },
    onError: notifyMutationError,
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.name) Object.assign(temp, { name: values.name });
    if (values?.status !== undefined && values?.status !== null) Object.assign(temp, { status: values.status });
    if (values?.type !== undefined && values?.type !== null) Object.assign(temp, { type: values.type });
    setParams(temp);
  };

  const handleOpenCreate = () => {
    setEditingFeature(null);
    formEdit.resetFields();
    formEdit.setFieldsValue({ status: 1, type: 1, service_type: 1, show_at_service_tab: 1, show_at_service_register_tab: 1 });
    setModalVisible(true);
  };

  const handleOpenEdit = (record) => {
    setEditingFeature(record);
    formEdit.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSave = () => {
    formEdit
      .validateFields()
      .then((values) => {
        if (editingFeature) {
          mutationUpdate.mutate({ ...values, id: editingFeature.id });
        } else {
          mutationCreate.mutate(values);
        }
      })
      // Form đã hiện lỗi dưới từng ô; bắt promise để không có "Uncaught (in promise)" ở console
      .catch(() => {});
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Tên (key)', dataIndex: 'name', key: 'name', width: 150 },
    { title: 'Tên hiển thị', dataIndex: 'display_name', key: 'display_name', width: 180 },
    {
      title: 'Icon', dataIndex: 'icon', key: 'icon', width: 70,
      render: (url) => url ? <img src={url} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : '-',
    },
    {
      title: 'Thumb', dataIndex: 'thumb', key: 'thumb', width: 70,
      render: (url) => url ? <img src={url} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : '-',
    },
    {
      title: 'Loại', dataIndex: 'type', key: 'type', width: 100,
      render: (v) => v === 1 ? <Tag color="blue">Có phí</Tag> : v === 2 ? <Tag color="green">Miễn phí</Tag> : '-',
    },
    { title: 'Thứ tự', dataIndex: 'order_place', key: 'order_place', width: 80 },
    {
      title: 'App Version', dataIndex: 'app_version', key: 'app_version', width: 110,
      render: (v) => v || '—',
    },
    {
      title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 100,
      render: (v) => v === 1 ? <Tag color="green">Active</Tag> : <Tag color="red">Deactive</Tag>,
    },
    {
      title: 'Hành động', key: 'action', width: 120,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>Sửa</Button>
          <Popconfirm title="Xóa tính năng này?" onConfirm={() => mutationDelete.mutate({ id: record.id })}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Quản lý tính năng</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>Thêm tính năng</Button>
      </div>

      <Form name="search_features" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="name" label={<Text>Tìm theo tên</Text>}>
              <Input placeholder="Nhập tên tính năng" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>}>
              <Select allowClear placeholder="Tất cả" options={STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={4}>
            <Form.Item name="type" label={<Text>Loại</Text>}>
              <Select allowClear placeholder="Tất cả" options={TYPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined /> Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['name', 'status', 'type'])).some((value) => value) && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined /> Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataFeatures?.data} totalItem={dataFeatures?.total} params={params} setParams={setParams} />

      {/* Modal Create/Edit */}
      <Modal
        title={editingFeature ? 'Sửa tính năng' : 'Thêm tính năng'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingFeature(null); formEdit.resetFields(); }}
        onOk={handleSave}
        okText={editingFeature ? 'Cập nhật' : 'Tạo mới'}
        width={720}
        confirmLoading={mutationCreate.isPending || mutationUpdate.isPending}
      >
        {/* KHÔNG đặt requiredMark={false} ở form nhập liệu: dấu * là dấu hiệu nhận biết trường
            bắt buộc (QA-13). Form tìm kiếm phía trên vẫn giữ requiredMark={false}. */}
        <Form form={formEdit} layout="vertical" scrollToFirstError={{ behavior: 'smooth', block: 'center' }}>
          <Row gutter={[12, 0]}>
            <Col span={12}>
              <Form.Item name="name" label="Tên (key)" rules={[{ required: true, message: 'Vui lòng nhập tên!' }]}>
                <Input placeholder="VD: xem_ngay_tot" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="display_name" label="Tên hiển thị">
                <Input placeholder="VD: Xem ngày tốt" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 0]}>
            <Col span={12}>
              <Form.Item name="type" label="Loại">
                <Select options={TYPE_OPTIONS} placeholder="Chọn loại" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="service_type" label="Loại dịch vụ">
                <Select options={SERVICE_TYPE_OPTIONS} placeholder="Chọn loại dịch vụ" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 0]}>
            <Col span={12}>
              <Form.Item name="status" label="Trạng thái">
                <Select options={STATUS_OPTIONS} placeholder="Chọn trạng thái" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="order_place" label="Thứ tự hiển thị">
                <InputNumber style={{ width: '100%' }} placeholder="Thứ tự" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 0]}>
            <Col span={12}>
              <Form.Item name="show_at_service_tab" label="Hiển thị ở tab dịch vụ">
                <Select options={SHOW_OPTIONS} placeholder="Chọn" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="show_at_service_register_tab" label="Hiển thị ở tab đăng ký">
                <Select options={SHOW_OPTIONS} placeholder="Chọn" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="app_version"
            label="Phiên bản app áp dụng"
            extra='Dạng dải "min:max" — 9.15:999 là từ 9.15 trở lên, 9.0:9.14 là trong khoảng. Để trống là mọi phiên bản. Lưu ý: 9.15 (không có dấu ":") nghĩa là đúng bản 9.15, app mới hơn sẽ không thấy tính năng.'
          >
            <Input placeholder="9.15:999" />
          </Form.Item>

          <Form.Item name="deeplink" label="Deeplink">
            <Input placeholder="Deeplink" />
          </Form.Item>
          <Form.Item name="des_deeplink" label="Deeplink mô tả">
            <Input placeholder="Deeplink mô tả" />
          </Form.Item>
          <Form.Item name="buy_deeplink" label="Deeplink mua gói">
            <Input placeholder="Deeplink mua gói" />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <TextArea rows={3} placeholder="Mô tả tính năng" maxLength={500} showCount />
          </Form.Item>

          <Row gutter={[12, 0]}>
            <Col span={8}>
              <Form.Item name="icon" label="Icon">
                <UploadButton buttonText="Chọn icon" />
              </Form.Item>
            </Col>
            <Col span={8}>
              {/* feature.thumb là NOT NULL không default: không chọn ảnh thì UploadButton trả
                  undefined, key bị bỏ khỏi payload và DB báo lỗi -> HTTP 500 (QA-05) */}
              <Form.Item name="thumb" label="Thumbnail" rules={[{ required: true, message: 'Vui lòng chọn thumbnail!' }]}>
                <UploadButton buttonText="Chọn thumbnail" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="des_image" label="Ảnh minh họa">
                <UploadButton buttonText="Chọn ảnh" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="ldp_content" label="LDP Content (HTML)">
            <TextArea rows={5} placeholder="Nội dung HTML cho landing page" />
          </Form.Item>

          <Form.Item name="metadata" label="Metadata (JSON)">
            <TextArea rows={3} placeholder='{"key": "value"}' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuanLyTinhNang;
